/* eslint-disable @typescript-eslint/no-explicit-any */
import { h, render } from "preact";

import { BerlinWideEmbed, ScopedEmbed } from "./BerlinWideEmbed.js";
import { DirectoryView } from "./DirectoryView.js";
import { LocaleContext, translate, type Locale } from "./messages.js";
import styles from "./styles.css";
import type { EmbedConfig, EventFilter } from "./types.js";

const DEFAULT_API_BASE = "https://cleanups.berlin";
// Override locally via data-cleanups-api-base="http://localhost:8001"
const SELECTOR = "[data-cleanups-org], [data-cleanups-berlin], [data-cleanups-region], [data-cleanups-initiatives]";
const MOUNTED = new WeakSet<Element>();

// Escape an operator-supplied URL before interpolating it into the footer's
// innerHTML, so a stray quote can't break out of the href attribute.
function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Captured at load time (before any await) so the map view can resolve
// self-hosted Leaflet assets relative to where embed.js itself is served.
const SCRIPT_SRC = (document.currentScript as HTMLScriptElement | null)?.src ?? "";

function vendorBaseFrom(apiBase: string): string {
  // Leaflet assets live next to embed.js on the same static origin.
  try {
    return new URL("/vendor/leaflet/", SCRIPT_SRC || apiBase).href;
  } catch {
    return `${apiBase.replace(/\/$/, "")}/vendor/leaflet/`;
  }
}

function parseFilter(raw: string): EventFilter {
  if (raw.endsWith("w")) {
    const v = parseInt(raw, 10);
    return { mode: "weeks", value: isNaN(v) ? 4 : v };
  }
  if (raw.endsWith("m")) {
    const v = parseInt(raw, 10);
    return { mode: "months", value: isNaN(v) ? 1 : v };
  }
  const v = parseInt(raw, 10);
  return { mode: "count", value: isNaN(v) ? 0 : v };
}

function readConfig(el: HTMLElement): EmbedConfig | null {
  const org = el.dataset.cleanupsOrg;
  const region = el.dataset.cleanupsRegion;
  const initiatives = el.dataset.cleanupsInitiatives !== undefined;
  // Must have one of: data-cleanups-org (org-scoped), data-cleanups-region
  // (district-scoped), data-cleanups-berlin (city-wide), or
  // data-cleanups-initiatives (initiative directory).
  if (!org && !region && !initiatives && el.dataset.cleanupsBerlin === undefined) return null;
  return {
    // Default DE; only an explicit data-cleanups-locale="en" switches to English.
    locale: el.dataset.cleanupsLocale === "en" ? "en" : "de",
    ...(org ? { org } : {}),
    ...(region ? { region } : {}),
    ...(initiatives ? { initiatives: true } : {}),
    apiBase: el.dataset.cleanupsApiBase || DEFAULT_API_BASE,
    upcoming: el.dataset.cleanupsUpcoming
      ? parseFilter(el.dataset.cleanupsUpcoming)
      : !org
        ? { mode: "weeks", value: 4 }
        : { mode: "count", value: 0 },
    past: el.dataset.cleanupsPast !== undefined
      ? parseFilter(el.dataset.cleanupsPast)
      : null,
    shareBase: el.dataset.cleanupsShareBase || undefined,
    moreUrl: el.dataset.cleanupsMoreUrl || undefined,
    view: el.dataset.cleanupsView === "tile" ? "tile" : el.dataset.cleanupsView === "list" ? "list" : undefined,
    showMap: el.dataset.cleanupsMap === "true",
    filterPosition: el.dataset.cleanupsFilterPosition === "below" ? "below" : "above",
    columns: (() => {
      const v = parseInt(el.dataset.cleanupsColumns ?? "", 10);
      return v >= 1 && v <= 6 ? (v as 1 | 2 | 3 | 4 | 5 | 6) : undefined;
    })(),
    vendorBase: vendorBaseFrom(el.dataset.cleanupsApiBase || DEFAULT_API_BASE),
  };
}

function hostFor(el: HTMLElement): HTMLElement {
  // The documented snippet carries the config attributes on the script tag
  // itself, but <script> cannot host a shadow root (attachShadow throws).
  // Render into a div inserted right after it instead.
  if (el.tagName === "SCRIPT") {
    const div = document.createElement("div");
    el.insertAdjacentElement("afterend", div);
    return div;
  }
  return el;
}

function mount(el: HTMLElement): void {
  if (MOUNTED.has(el)) return;
  const config = readConfig(el);
  if (!config) return;

  const host = hostFor(el);
  const shadow = host.attachShadow ? host.attachShadow({ mode: "open" }) : null;
  const root = shadow ?? document.createElement("div");
  if (!shadow) host.appendChild(root);

  const style = document.createElement("style");
  style.textContent = styles;
  root.appendChild(style);

  const container = document.createElement("div");
  container.className = "cb-root";
  root.appendChild(container);

  const list = document.createElement("div");
  container.appendChild(list);
  // The map is an optional layer above the list/tile, not a separate view.
  // LocaleContext makes the active locale available to every component.
  const tree = config.initiatives
    ? h(DirectoryView, { config })
    : config.org
    ? h(ScopedEmbed, { config })
    : h(BerlinWideEmbed, { config });
  render(h(LocaleContext.Provider, { value: config.locale as Locale }, tree), list);

  const footer = document.createElement("div");
  footer.className = "cb-footer";
  const locale = config.locale as Locale;
  const brandLink =
    '<a href="https://cleanups.berlin/" target="_blank" rel="noopener"><b><span style="color:var(--cb-brand)">cleanups</span>.berlin</b></a>';
  if (config.moreUrl) {
    // Operator points the "see all" link at their own page (keeps visitors on
    // their site); cleanups.berlin stays visible as a small attribution below.
    const moreLabel = translate(locale, config.initiatives ? "footerMoreDirectory" : "footerMore");
    const href = escapeAttr(config.moreUrl);
    footer.innerHTML =
      `<a href="${href}" target="_blank" rel="noopener">${moreLabel}</a>` +
      `<span class="cb-footer-by">${translate(locale, "footerProvidedBy")}${brandLink}</span>`;
  } else {
    footer.innerHTML =
      translate(locale, config.initiatives ? "footerPrefixDirectory" : "footerPrefix") + brandLink;
  }
  container.appendChild(footer);

  MOUNTED.add(el);
}

function mountAll(): void {
  document.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
    try {
      mount(el);
    } catch (err) {
      // One broken host must not keep the remaining embeds from mounting.
      console.error("[cleanups-embed] mount failed:", err);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountAll, { once: true });
} else {
  mountAll();
}

// Expose a tiny manual API so host pages can re-mount after dynamic insertions
(globalThis as any).CleanupsEmbed = { mount: mountAll };
